/**
 * 3D device markers for the Viewer: one InstancedMesh per device kind with a
 * simple distinctive primitive (detector sphere, sounder cone, strobe box,
 * pull-station pentagonal prism, FACP/annunciator octahedron "star"), colored
 * to match the 2D plan markers.
 *
 * Owns a THREE.Group the Viewer adds to its scene. Pure three.js object
 * management — no renderer needed, so the layer is unit-testable in jsdom.
 *
 * Coordinates: devices arrive in plan meters (x_m east, y_m north, z_m up);
 * three.js is y-up, so instances are placed at (x, z, y) — the same mapping
 * the Fragments building mesh uses (see planToThree). The per-model offset
 * between plan coords and the Fragments mesh is applied on `group`.
 */
import * as THREE from "three";
import { markerColor, markerShape, type MarkerShape } from "../mep/markers";

/** What the layer needs per device (subset of `MepDevice`). */
export interface LayerDevice {
  id: string;
  kind: string;
  x_m: number;
  y_m: number;
  z_m: number;
  storey_id: string | null;
}

/** Marker outer radius in meters. */
const MARKER_RADIUS = 0.22;

function geometryFor(shape: MarkerShape): THREE.BufferGeometry {
  switch (shape) {
    case "circle":
      return new THREE.SphereGeometry(MARKER_RADIUS, 12, 8);
    case "triangle":
      return new THREE.ConeGeometry(MARKER_RADIUS, MARKER_RADIUS * 2, 3);
    case "square":
      return new THREE.BoxGeometry(MARKER_RADIUS * 1.6, MARKER_RADIUS * 1.6, MARKER_RADIUS * 1.6);
    case "pentagon":
      return new THREE.CylinderGeometry(MARKER_RADIUS, MARKER_RADIUS, MARKER_RADIUS * 1.2, 5);
    case "star":
      return new THREE.OctahedronGeometry(MARKER_RADIUS * 1.3);
    case "diamond":
      return new THREE.OctahedronGeometry(MARKER_RADIUS);
  }
}

export class DeviceLayer {
  /** Added to the Viewer's scene; everything the layer renders lives here. */
  readonly group = new THREE.Group();

  private devices: LayerDevice[] = [];
  private meshes = new Map<string, THREE.InstancedMesh>();
  private hiddenKinds = new Set<string>();
  private storeyFilter: string | null = null;

  // Shared geometry/material per device kind, reused across rebuilds (kind
  // toggles / storey filter). Created lazily; disposed only in dispose(),
  // never in clearMeshes() — the mesh primitives depend only on the kind.
  private geoCache = new Map<string, THREE.BufferGeometry>();
  private matCache = new Map<string, THREE.MeshStandardMaterial>();

  constructor() {
    this.group.name = "istari-device-layer";
  }

  private geometryForKind(kind: string): THREE.BufferGeometry {
    let geo = this.geoCache.get(kind);
    if (!geo) {
      geo = geometryFor(markerShape(kind));
      this.geoCache.set(kind, geo);
    }
    return geo;
  }

  private materialForKind(kind: string): THREE.MeshStandardMaterial {
    let mat = this.matCache.get(kind);
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(markerColor(kind)) });
      this.matCache.set(kind, mat);
    }
    return mat;
  }

  /** Replace all device instances (called after generation/regeneration). */
  setDevices(devices: readonly LayerDevice[]): void {
    this.devices = [...devices];
    this.rebuild();
  }

  /** Toggle visibility of one device kind (layer panel checkboxes). */
  setKindVisible(kind: string, visible: boolean): void {
    if (visible) {
      this.hiddenKinds.delete(kind);
    } else {
      this.hiddenKinds.add(kind);
    }
    this.rebuild();
  }

  /**
   * Restrict markers to one storey (`null` shows all). Matches the server
   * storey id on the device rows; called alongside the Viewer's storey
   * isolation so devices respect it.
   */
  setStoreyFilter(storeyId: string | null): void {
    this.storeyFilter = storeyId;
    this.rebuild();
  }

  /** Device kinds present, sorted (deterministic for UI toggles). */
  kinds(): string[] {
    return [...new Set(this.devices.map((d) => d.kind))].sort();
  }

  /** Currently rendered instance count per kind (visible markers). */
  visibleCount(kind: string): number {
    return this.meshes.get(kind)?.count ?? 0;
  }

  private visibleDevices(): Map<string, LayerDevice[]> {
    const byKind = new Map<string, LayerDevice[]>();
    for (const device of this.devices) {
      if (this.hiddenKinds.has(device.kind)) continue;
      if (this.storeyFilter !== null && device.storey_id !== this.storeyFilter) continue;
      const list = byKind.get(device.kind) ?? [];
      list.push(device);
      byKind.set(device.kind, list);
    }
    return byKind;
  }

  private rebuild(): void {
    this.clearMeshes();
    const byKind = this.visibleDevices();
    const matrix = new THREE.Matrix4();
    // Sorted kinds → deterministic scene-graph order.
    for (const kind of [...byKind.keys()].sort()) {
      const list = byKind.get(kind)!;
      const mesh = new THREE.InstancedMesh(
        this.geometryForKind(kind),
        this.materialForKind(kind),
        list.length,
      );
      mesh.name = `devices:${kind}`;
      list.forEach((device, i) => {
        // Plan (x east, y north, z up) → three (x, y up, z) — same mapping
        // as planToThree; per-model offset comes from the group transform.
        matrix.makeTranslation(device.x_m, device.z_m, device.y_m);
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.meshes.set(kind, mesh);
      this.group.add(mesh);
    }
  }

  private clearMeshes(): void {
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh);
      // Geometry and material are shared/cached per kind and reused across
      // rebuilds, so they are NOT disposed here — only in dispose(). This
      // frees the InstancedMesh's own instance buffers.
      mesh.dispose();
    }
    this.meshes.clear();
  }

  dispose(): void {
    this.clearMeshes();
    for (const geo of this.geoCache.values()) geo.dispose();
    for (const mat of this.matCache.values()) mat.dispose();
    this.geoCache.clear();
    this.matCache.clear();
    this.devices = [];
  }
}
